import * as React from 'react';
import {
    TextInput, SelectInput, FileInput, BooleanInput,
    SimpleForm, Link,
    useRecordContext, useNotify, usePermissions,
} from 'react-admin';
import ResponsiveMolstar from '../commons/ResponsiveMolstar';
import { useFormContext } from "react-hook-form";
import { Box, Divider, Tabs, Tab, Stack, Button, Typography, LinearProgress, Grid } from '@mui/material';
import { validateForm } from './utils';
import SectionTitle from '../commons/SectionTitle';
import { workerTypeChoices, statusChoices, workerTypeChoicesIdToName } from './utils';
import { Fragment, useState, useEffect } from 'react';
import BasicTabs from '../commons/BasicTabs';
import { deflateFromFile, deflateFromGzipUrl, inflateToFile } from '../utils/ioFileTools';

export const JobForm = ({ workerType: _workerType, ...props }) => {
    const record = useRecordContext();
    const { permissions } = usePermissions();
    const [workerType, setWorkerType] = useState(_workerType ?? record?.workerType ?? workerTypeChoices[0].id);
    const [fileStatus, setFileStatus] = useState(-1);
    const [rcsbPdbIdInfo, setRcsbPdbIdInfo] = useState(null);
    const [molStarPDBFile, setMolStarPDBFile] = useState(null);
    const [inflateSucceed, setInflateSucceed] = useState(null);

    let formContext;
    const setFormContext = (ctx) => {
        formContext = ctx
    }

    useEffect(() => {
        formContext.setValue('workerType', workerType);
        formContext.setValue('inputData.proteinFile.suffix', 'pdb');
        formContext.setValue('originalRecord', record);
        handleProteinFileContentChange();
        return () => {
            // https://stackoverflow.com/a/65007703/16407115
            setMolStarPDBFile(null);
        };
    }, [record])

    const handleWorkerTypeChange = (_event, value) => {
        setWorkerType(value)
    }

    const handleProteinTabChange = () => {
        setFileStatus(-1)
        formContext.setValue('inputData.proteinFile.content', undefined);
        setRcsbPdbIdInfo(null);
        if (formContext.getValues('name').startsWith('RCSB-PDB-')) {
            formContext.setValue('name', undefined);
        }
    }

    const notify = useNotify();
    const setGzipedBase64 = (gzipedBase64Promise) => (
        gzipedBase64Promise.then(
            (gzipedBase64) => {
                formContext.setValue('inputData.proteinFile.content', gzipedBase64);
                return gzipedBase64.length;
            }
        ).then(
            (len) => {
                notify(`Read succeeded: length of GZIP Base64 ${len.toLocaleString()}`, { type: "success" });
                setFileStatus(1);
                handleProteinFileContentChange();
            }
        ).catch(
            (error) => {
                notify(`Read failed: ${error}`, { type: "error" })
                setFileStatus(-1);
            }
        )
    )

    const handleProteinFileUpload = (file) => {
        if (!file) {
            notify("Read failed: File is too large", { type: "error" });
            return false
        }
        setFileStatus(0);
        setGzipedBase64(deflateFromFile(file))
    }

    const handleClickRcsbPdbId = () => {
        const rcsbPdbId = formContext.getValues('rcsbPdbId');
        const prasedRcsbPdbId = rcsbPdbId.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
        if (!rcsbPdbId || prasedRcsbPdbId !== rcsbPdbId || rcsbPdbId.length !== 4) {
            notify("Fetch failed: Illegal RCSB PDB ID", { type: "error" });
            return false
        }
        const rcsbPdbDownUrl = `https://files.rcsb.org/download/${rcsbPdbId}.pdb.gz`
        const rcsbPdbPageUrl = `https://www.rcsb.org/structure/${rcsbPdbId}`
        setRcsbPdbIdInfo(
            <>
                <Link target="_blank" to={`/redirect?to=${rcsbPdbDownUrl}`}>{rcsbPdbDownUrl}</Link>
                &emsp;
                <Link target="_blank" to={`/redirect?to=${rcsbPdbPageUrl}`}>{`[PDB]`}</Link>
            </>
        )
        setFileStatus(0)
        setGzipedBase64(deflateFromGzipUrl(rcsbPdbDownUrl)).then(
            () => {
                if (!formContext.getValues('name')) {
                    formContext.setValue('name', `RCSB-PDB-${rcsbPdbId}`)
                }
            }
        )
    }

    const handleProteinFileContentChange = async () => {
        if (!formContext) { return null }
        const suffix = formContext.getValues('inputData.proteinFile.suffix');
        const content = formContext.getValues('inputData.proteinFile.content');
        // console.log(suffix, content);
        if (!(suffix && content)) { return null }
        inflateToFile(content, `proteinFile.${suffix}`, suffix).then(
            (rawFile) => {
                setMolStarPDBFile(rawFile)
            })
            .then(() => setInflateSucceed(true))
            .catch(() => setInflateSucceed(false))
    }

    const proteinFileMaxSize = 16; // MB

    return (
        <Fragment>
            <Tabs variant="fullWidth" centered
                value={workerType} indicatorColor="primary" onChange={handleWorkerTypeChange}
            >
                {workerTypeChoices.map(choice => (
                    <Tab key={choice.id} value={choice.id} sx={{ textTransform: "none" }} label={choice.name} />
                ))}
            </Tabs>
            <Divider />

            <SimpleForm validate={validateForm(record)} shouldUnregister {...props}>
                <SenseFormContext callback={setFormContext} />

                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <SectionTitle label="Config" />

                        <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
                            <Box flex={3.5} mr={{ xs: 0, sm: '0.5em' }}>
                                <SelectInput source="workerType" label="Worker Type" isRequired fullWidth choices={workerTypeChoices} disabled />
                            </Box>
                            <Box flex={2} ml={{ xs: 0, sm: '0.5em' }} display="flex" justifyContent="flex-end">
                                <BooleanInput source='public' label='Public (Admin)' defaultValue={false}
                                    isRequired fullWidth sx={{ marginTop: 1.6 }} disabled={permissions !== "admin"}
                                />
                            </Box>
                        </Box>

                        <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
                            <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
                                <TextInput source="name" isRequired fullWidth />
                            </Box>
                            <Box flex={1} ml={{ xs: 0, sm: '0.5em' }}>
                                <SelectInput source="status" choices={statusChoices} defaultValue="queued" isRequired fullWidth />
                            </Box>
                        </Box>

                        <SectionTitle label="Protein" />

                        <BasicTabs boxSx={{ paddingTop: 1, minHeight: 140 }} onChangeTab={handleProteinTabChange}>
                            <Stack label="Upload">
                                <Stack minHeight={140}>
                                    <Typography variant="body1">
                                        {`Upload protein file (.pdb) < ${proteinFileMaxSize} MB`}
                                    </Typography>
                                    <Box pt="1em" />
                                    <FileInput source="proteinFile" label={false}
                                        onChange={handleProteinFileUpload}
                                        maxSize={proteinFileMaxSize * (1 << 20)}
                                    />
                                </Stack>
                                <StatusLinearProgress status={fileStatus} />
                            </Stack>

                            <Stack label="RCSB PDB">
                                <Stack minHeight={140}>
                                    <Typography variant="body1">
                                        {`Fetch protein file from `}
                                        <Link target="_blank" to={`/redirect?to=https://www.rcsb.org/`}>RCSB Protein Data Bank</Link>
                                        {` by Entry ID`}
                                    </Typography>
                                    <Box pt="1em" />
                                    <Box display={{ xs: 'block', sm: 'flex', width: '100%' }}>
                                        <Box flex={4}>
                                            <TextInput source="rcsbPdbId" label="RCSB PDB ID" helperText={false}
                                                format={value => value == null ? '' : value}
                                                parse={value => value === '' ? null : value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4)}
                                                onChange={() => (setRcsbPdbIdInfo(null))}
                                                isRequired fullWidth />
                                        </Box>
                                        <Box flex={2} />
                                        <Box flex={2}>
                                            <Button variant="outlined" sx={{ marginTop: 1.6 }} onClick={handleClickRcsbPdbId}>
                                                Fetch
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Typography>{rcsbPdbIdInfo}</Typography>
                                </Stack>
                                <StatusLinearProgress status={fileStatus} />
                            </Stack>
                        </BasicTabs>

                        <TextInput source="inputData.proteinFile.content"
                            label="Protein file GZIP Base64 (Automatically generate or paste)"
                            format={value => value == null ? '' : value.length > 1 << 8 ? `Length of GZIP Base64: ${value.length.toLocaleString()}` : value}
                            onChange={handleProteinFileContentChange}
                            isRequired fullWidth resettable
                            helperText={inflateSucceed === null ? 'Waiting for verify' : inflateSucceed ? "Good! Verification passed" : "Bad! Verification failed"}
                        />
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={7}>
                        {/* {molStarPDBFile ? <Molstar file={molStarPDBFile} dimensions={[800, 600]} useInterface showAxes /> : null} */}
                        {/* <Molstar file={molStarPDBFile} dimensions={[800, 600]} useInterface showAxes /> */}
                        {molStarPDBFile && <ResponsiveMolstar file={molStarPDBFile} />}
                    </Grid>
                </Grid>
            </SimpleForm >
        </Fragment>
    );
}

export const JobTitle = () => {
    const record = useRecordContext();
    if (record) {
        return <span>{`${workerTypeChoicesIdToName[record.workerType]} "${record.name}"`}</span>;
    } else {
        return <span>{'Job "Unknow"'}</span>;
    }
};

const SenseFormContext = ({ callback }) => {
    callback(useFormContext());
    return null
}

const StatusLinearProgress = ({ status }) => {
    if (status === -1) {
        return <LinearProgress color="secondary" variant="determinate" value={0} />
    }
    if (status === 0) {
        return <LinearProgress color="secondary" />
    }
    if (status === 1) {
        return <LinearProgress color="secondary" variant="determinate" value={100} />
    }
}